import { AppService } from './../../../../../core/app.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { ActiviteService } from '../../../../../services/activite/activite.service';
import { toCamelCase, toSnakeCaseUpper } from '../../../../../utils/helpers';
import { routeAlias } from '../../../../../../routesalias';
import { FormsModule } from '@angular/forms';
import { SteponeactivitiesComponent } from '../../../../../components/activities/steponeactivities/steponeactivities.component';
import { SteptwoactivitiesComponent } from '../../../../../components/activities/steptwoactivities/steptwoactivities.component';
import { StepthreeactivitiesComponent } from '../../../../../components/activities/stepthreeactivities/stepthreeactivities.component';
import { StepfouractivitiesComponent } from '../../../../../components/activities/stepfouractivities/stepfouractivities.component';
import { RecapactivitiesComponent } from '../../../../../components/activities/recapactivities/recapactivities.component';
import { CommonModule } from '@angular/common';
import { LoaderComponent } from '../../../../../components/ui/loader/loader.component';

interface StepperState {
  activeStepId: string;
  completedSteps: string[];
  showPmpStep: boolean;
}

@Component({
  selector: 'app-edit',
  imports: [
    FormsModule,
    RouterLink,
    SteponeactivitiesComponent,
    SteptwoactivitiesComponent,
    StepthreeactivitiesComponent,
    StepfouractivitiesComponent,
    CommonModule,
    RecapactivitiesComponent,
    LoaderComponent
  ],
  templateUrl: './edit.component.html',
  styleUrl: './edit.component.css'
})
export class EditActivityPage implements OnInit, OnDestroy {
  // Étapes de base, l'étape PMP sera insérée dynamiquement
  baseSteps = [
    { id: 'generales', label: 'Informations Générales' },
    { id: 'activites', label: "Informations sur l'Activités" },
    { id: 'budgets', label: "Informations sur le budget de l'activité" },
    { id: 'recap', label: 'Récapitulatif' }
  ];
  steps: { id: string; label: string; }[] = []; // Tableau d'étapes dynamique

  activeStepId: string = '';
  activity: any = {}
  public isLoading: boolean = false;
  public showPmpStep: boolean = false; // Contrôle l'affichage de l'étape PMP
  private isTypeNouvelleActivite: boolean = false; // Pour suivre la condition de l'étape 1
  private isModePmp: boolean = false;
  public completedSteps: string[] = []; // Suivi des étapes complétées

  public activityId: string | null = ""

  private queryParamsSubscription: Subscription | undefined;
  private stepperStateKey = 'activityStepperState'; // Clé pour localStorage de l'état du stepper
  private activityDataKey = 'activity'; // Clé pour localStorage des données de l'activité

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private activiteService: ActiviteService,
    private AppService: AppService
  ) { }

  ngOnInit(): void {
    this.activityId = this.route.snapshot.paramMap.get('id');
    if (this.activityId) {
      this.isLoading = true;
      this.activiteService.getById(this.activityId).subscribe({
        next: (response) => {
          //console.log("Données reçues pour l'édition de l'activité :", response.data);
          // 1. Transformer les données de l'API dans la structure attendue par le stepper
          this.activity = this.transformApiDataToActivityState(response.data);

          // 2. Sauvegarder cet état transformé dans localStorage pour que les composants enfants puissent le lire
          localStorage.setItem(this.activityDataKey, JSON.stringify(this.activity));

          // 3. Initialiser l'état du stepper (toutes les étapes sont considérées comme complétées)
          this.initializeStepperStateForEdit(response.data);

          // 4. Continuer avec la logique d'initialisation standard du stepper
          this.setupStepperNavigation();
          this.isLoading = false;
        },
        error: (err) => {
          console.error("Erreur lors du chargement de l'activité", err);
          this.isLoading = false;
          // Rediriger ou afficher une erreur
        }
      });
    } else {
      console.error("Aucun ID d'activité fourni.");
      this.initializeDefaultStepperState();
      this.setupStepperNavigation();
    }
  }

  // ─── Méthodes à ajouter dans EditActivityPage ───────────────────────────────
// (utilisées par le template HTML du stepper)

/** Index 0-based de l'étape active (utilisé par le compteur "X / N") */
getActiveStepIndex(): number {
  return this.steps.findIndex(s => s.id === this.activeStepId);
}

/** Label de l'étape active affiché sous le stepper */
getCurrentStepLabel(): string {
  return this.steps.find(s => s.id === this.activeStepId)?.label ?? '';
}

/**
 * Pourcentage de progression (0–100) pour la barre en haut du stepper.
 * Vaut 100 % uniquement sur la dernière étape (recap).
 */
getProgressPercent(): number {
  const total = this.steps.length;
  if (total <= 1) return 0;
  const index = this.getActiveStepIndex();
  return Math.round((index / (total - 1)) * 100);
}

  private setupStepperNavigation(): void {
    this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
      const requestedStepId = params['step'];
      const currentStepIndex = this.steps.findIndex(s => s.id === requestedStepId);

      const isRecapAvailable = requestedStepId === 'recap' &&
        ((!this.showPmpStep && this.completedSteps.includes('budgets')) ||
          (this.showPmpStep && this.completedSteps.includes('stepfour')));

      if (requestedStepId && currentStepIndex !== -1) {
        const isStepAvailable = this.completedSteps.includes(requestedStepId) || requestedStepId === this.activeStepId || isRecapAvailable;

        if (isStepAvailable) {
          this.activeStepId = requestedStepId;
        } else {
          this.router.navigate([], { queryParams: { step: this.activeStepId }, relativeTo: this.route, replaceUrl: true });
        }
      } else {
        this.router.navigate([], { queryParams: { step: this.activeStepId }, relativeTo: this.route, replaceUrl: true });
      }
      this.saveStepperState();
    });
  }


  private transformApiDataToActivityState(data: any): any {
    // ... votre logique existante de transformation ...
    const activityState: any = {
      generales: { taches: [] },
      activites: { structureAssociee: [] },
      budgets: {},
      stepfour: {}
    };

    // --- Mapper les champs de haut niveau ---
    activityState.generales.typeInterventionSelect = data.parent?.id || '';
    activityState.generales.activities = data.titre || '';
    activityState.generales.description = data.objectif || '';

    // Initialiser les drapeaux pour la visibilité de l'étape PMP
    let isModePmpFromData = false;

    // --- Mapper le tableau 'caracteristiques' ---
    data.caracteristiques.forEach((car: { code: string; valeur: any }) => {
      const key = toCamelCase(car.code);
      switch (key) {
        // Etape 1: generales
        case 'typeActivite':
          activityState.generales.typeActivitySelect = car.valeur;
          break;
        // Etape 2: activites
        case 'supervisor':
        case 'structureResponsable':
          activityState.activites[key] = car.valeur;
          break;
        case 'modeExecution':
          activityState.activites[key] = car.valeur;
          if (car.valeur === 'PMP') { // Assurez-vous que 'PMP' est la valeur correcte
            isModePmpFromData = true;
          }
          break;
        case 'structassocie':
          activityState.activites.structureAssociee.push(car.valeur);
          break;
        // Etape 3: budgets
        case 'montantEstimatif':
        case 'imputationBudgetaire':
        case 'horizon':
        case 'coutPrevuN':
        case 'coutPrevuN1':
        case 'previsionDecaissementT4N':
        case 'previsionDecaissementN2':
        case 'montantDecaisseAu30SeptN':
          activityState.budgets[key] = car.valeur;
          break;
        // Etape 4: PMP/stepfour
        case 'delaiNeccessairePassationMarche':
        case 'dureePrevisionExecutionMarche':
        case 'echeanceSatisfaction':
        case 'modePassationMarche': // Si cette caractéristique existe, cela indique une étape PMP
          activityState.stepfour[key] = car.valeur;
          break;
        default:
          // Pour les autres caractéristiques non mappées directement à un formulaire spécifique,
          // vous pouvez les stocker dans stepfour ou un autre endroit si nécessaire
          activityState.stepfour[key] = car.valeur;
          break;
      }
    });

    // --- Mapper le tableau 'taches' ---
    const monthNamesMap: { [key: string]: number } = { "JANVIER": 0, "FEVRIER": 1, "MARS": 2, "AVRIL": 3, "MAI": 4, "JUIN": 5, "JUILLET": 6, "AOUT": 7, "SEPTEMBRE": 8, "OCTOBRE": 9, "NOVEMBRE": 10, "DECEMBRE": 11 };
    if (data.taches && Array.isArray(data.taches)) {
      activityState.generales.taches = data.taches.map((tache: any) => {
        const formTache = {
          titreTache: tache.titre,
          poids: '',
          chronogrammes: Array(12).fill(false)
        };
        if (tache.caracteristiques) { // Vérifiez si caracteristiques existe pour la tâche
          tache.caracteristiques.forEach((car: { code: string; valeur: any }) => {
            if (car.code === 'POIDS') {
              formTache.poids = car.valeur;
            } else if (monthNamesMap[car.code] !== undefined) {
              const monthIndex = monthNamesMap[car.code];
              formTache.chronogrammes[monthIndex] = car.valeur === 'true' || car.valeur === true;
            }
          });
        }
        return formTache;
      });
    }

    // Mettre à jour les propriétés du composant après la transformation
    this.isTypeNouvelleActivite = activityState.generales?.typeActivitySelect === 'Nouvelle activité';
    // isModePmp est déterminé par la présence de MODE_EXECUTION='PMP' ou des champs PMP
    this.isModePmp = isModePmpFromData || Object.keys(activityState.stepfour).length > 0;

    return activityState;
  }

  // Initialise l'état du stepper pour l'édition
  private initializeStepperStateForEdit(apiData: any): void {
    // Déterminer l'état initial des drapeaux basés sur les données de l'API
    this.isTypeNouvelleActivite = this.activity.generales?.typeActivitySelect === 'Nouvelle activité';
    this.isModePmp = this.activity.activites?.modeExecution === 'PMP' || Object.keys(this.activity.stepfour).length > 0;

    this.showPmpStep = this.isTypeNouvelleActivite && this.isModePmp;
    this.updateStepsArray(); // Mettre à jour les étapes une première fois

    // Pour l'édition, on peut considérer toutes les étapes comme complétées initialement
    // ou au moins la première étape, permettant à l'utilisateur de naviguer librement.
    // Ici, nous marquerons toutes les étapes comme complétées pour une navigation facile.
    this.completedSteps = this.steps.map(step => step.id);

    // Essayer de retrouver l'étape depuis les query params ou définir sur la première étape
    const initialStepFromUrl = this.route.snapshot.queryParams['step'];
    if (initialStepFromUrl && this.steps.some(s => s.id === initialStepFromUrl)) {
      this.activeStepId = initialStepFromUrl;
    } else {
      this.activeStepId = this.steps[0]?.id || 'generales'; // Active la première étape par défaut
    }
    this.saveStepperState();
  }

  // Initialise l'état du stepper par défaut si aucun ID d'activité n'est fourni
  private initializeDefaultStepperState(): void {
    this.activity = {
      generales: { typeInterventionSelect: '', typeActivitySelect: '' },
      activites: {},
      budgets: {},
      stepfour: {}
    };
    this.activeStepId = 'generales';
    this.completedSteps = [];
    this.isTypeNouvelleActivite = false;
    this.isModePmp = false;
    this.showPmpStep = false;
    this.updateStepsArray();
    this.saveStepperState();
  }



  ngOnDestroy(): void {
    this.queryParamsSubscription?.unsubscribe();
  }

  loadActivityById(id: string): void {
    this.activiteService.getById(id).subscribe({
      next: (response: any) => {
        //console.log("Données reçues pour l'édition de activities :", response.data)
      },
      error: (error) => {
        console.error('Erreur lors du chargement de l\'activité par ID :', error);
      }
    })
  }

  // Met à jour le tableau des étapes (avec ou sans l'étape PMP)
  private updateStepsArray(): void {
    this.steps = [...this.baseSteps]; // Commencer avec les étapes de base
    if (this.showPmpStep) {
      const budgetsIndex = this.steps.findIndex(s => s.id === 'recap');
      if (budgetsIndex !== -1) {
        // Insérer l'étape PMP *avant* l'étape 'budgets'
        this.steps.splice(budgetsIndex, 0, { id: 'stepfour', label: 'Informations Marché' });
      } else {
        // Fallback si 'budgets' n'est pas trouvé (ajouter à la fin avant recap)
        const recapIndex = this.steps.findIndex(s => s.id === 'recap');
        if (recapIndex !== -1) {
          this.steps.splice(recapIndex, 0, { id: 'stepfour', label: 'Informations Marché' });
        } else {
          this.steps.push({ id: 'stepfour', label: 'Informations Marché' });
        }
      }
    }
  }

  // Sauvegarde l'état actuel du stepper dans localStorage
  private saveStepperState(): void {
    const state: StepperState = {
      activeStepId: this.activeStepId,
      completedSteps: this.completedSteps,
      showPmpStep: this.isTypeNouvelleActivite && this.isModePmp
    };
    localStorage.setItem(this.stepperStateKey, JSON.stringify(state));
  }

  // Gère les mises à jour des données des étapes enfants et la navigation
  updateActivity(stepData: any, stepId: string): void {
    // Mettre à jour les données de l'activité
    if (stepId === 'generales') {
      this.activity.generales = stepData;
    } else if (stepId === 'activites') {
      this.activity.activites = stepData;
    } else if (stepId === 'budgets') {
      this.activity.budgets = stepData;
    } else if (stepId === 'stepfour') {
      this.activity.stepfour = stepData;
    }

    localStorage.setItem(this.activityDataKey, JSON.stringify(this.activity));

    // Marquer l'étape actuelle comme complétée
    if (!this.completedSteps.includes(stepId)) {
      this.completedSteps.push(stepId);
    }

    // Navigation : uniquement pour les étapes qui ne naviguent pas elles-mêmes.
    // steptwoactivities et stepthreeactivities gèrent leur propre navigation.
    // steponeactivities et stepfouractivities délèguent la navigation au parent.
    if (stepId === 'generales') {
      this.activeStepId = 'activites';
      this.router.navigate([], { queryParams: { step: 'activites' }, relativeTo: this.route });
    } else if (stepId === 'stepfour') {
      this.activeStepId = 'recap';
      this.router.navigate([], { queryParams: { step: 'recap' }, relativeTo: this.route });
    }
    // Pour 'activites' et 'budgets', la navigation est déjà faite par les composants enfants.

    this.saveStepperState();
  }

  // Gère le changement du type d'activité (émis par SteponeactivitiesComponent)
  handleTypeActivityChange(isNouvelleActivite: boolean): void {
    this.isTypeNouvelleActivite = isNouvelleActivite;
    this.updatePmpStepVisibility();
  }
  handlePmpModeChange(isPmp: boolean): void {
    this.isModePmp = isPmp;
    this.updatePmpStepVisibility();
  }
  // Méthode centralisée pour gérer la visibilité de l'étape PMP
  private updatePmpStepVisibility(): void {
    const shouldShow = this.isTypeNouvelleActivite && this.isModePmp;

    // Si l'état de visibilité change
    if (this.showPmpStep !== shouldShow) {
      this.showPmpStep = shouldShow;
      this.updateStepsArray(); // Reconstruire le tableau des étapes

      // Si l'étape PMP est cachée alors que l'utilisateur est dessus
      if (!this.showPmpStep && this.activeStepId === 'stepfour') {
        this.activeStepId = 'budgets'; // Rediriger vers une étape sûre
        this.router.navigate([], { queryParams: { step: 'budgets' }, relativeTo: this.route, replaceUrl: true });
        // Effacer les données de l'étape 4
        this.activity.stepfour = {};
        localStorage.setItem(this.activityDataKey, JSON.stringify(this.activity));
      }
      this.saveStepperState();
    }
  }

  // Nouvelle méthode pour gérer la soumission finale depuis le composant de récapitulatif
  handleFinalSubmit(): void {
    this.onSubmitActivity();
  }

  onSubmitActivity(): void {
    //console.log(this.activity)
    const caracteristiquesArray: { code: string; valeur: any }[] = [];

    const excludedFieldForActivites = ['structureAssociee'];
    Object.keys(this.activity.activites).forEach(key => {
      // Exclure les champs spécifiés
      if (!excludedFieldForActivites.includes(key)) {
        caracteristiquesArray.push({
          code: toSnakeCaseUpper(key),
          valeur: this.activity.activites[key] ?? ""
        });
      }
    });

    if (this.activity.activites.structureAssociee && Array.isArray(this.activity.activites.structureAssociee)) {
      this.activity.activites.structureAssociee.forEach((structureId: string) => {
        caracteristiquesArray.push({
          code: "STRUCTASSOCIE",
          valeur: structureId ?? ''
        });
      });
    }

    Object.keys(this.activity.budgets).forEach(key => {
      // Exclure les champs spécifiés
      caracteristiquesArray.push({
        code: toSnakeCaseUpper(key),
        valeur: this.activity.budgets[key] ?? ""
      });
    });

    Object.keys(this.activity.stepfour).forEach(key => {
      // Exclure les champs spécifiés
      caracteristiquesArray.push({
        code: toSnakeCaseUpper(key),
        valeur: this.activity.stepfour[key] ?? ""
      });
    });
    /* //console.log(caracteristiquesArray)
    return */


    caracteristiquesArray.push(
      {
        code: "TYPE_ACTIVITE",
        valeur: this.activity.generales.typeActivitySelect ?? ''
      },
    );
    // Transformation des tâches
    const transformedTaches: any[] = [];
    const monthNames = [
      "JANVIER", "FEVRIER", "MARS", "AVRIL", "MAI", "JUIN",
      "JUILLET", "AOUT", "SEPTEMBRE", "OCTOBRE", "NOVEMBRE", "DECEMBRE"
    ];

    if (this.activity.generales.taches && Array.isArray(this.activity.generales.taches)) {
      this.activity.generales.taches.forEach((tache: any) => {
        const tacheCaracteristiques: { code: string; valeur: any }[] = [];

        // Ajouter le poids
        if (tache.poids !== undefined) {
          tacheCaracteristiques.push({
            code: "POIDS",
            valeur: tache.poids ?? ''
          });
        }

        // Ajouter les chronogrammes mensuels
        if (tache.chronogrammes && Array.isArray(tache.chronogrammes)) {
          tache.chronogrammes.forEach((isScheduled: boolean, index: number) => {
            if (index < monthNames.length) {
              tacheCaracteristiques.push({
                code: monthNames[index],
                valeur: isScheduled ? true : false // Convertir en chaîne "true" ou "false"
              });
            }
          });
        }

        transformedTaches.push({
          titre: tache.titreTache ?? '',
          caracteristiques: tacheCaracteristiques
        });
      });
    }

    const trueSendingActivity = {
      parent: this.activity.generales.typeInterventionSelect ?? '',
      titre: this.activity.generales.activities ?? '',
      objectif: this.activity.generales.description ?? '',
      typeActivite: "ACTIVITE",
      caracteristiques: caracteristiquesArray,
      taches: transformedTaches,
      applicationId: this.AppService.getAppId()!
    }
    /* //console.log(trueSendingActivity)
    return; */


    if (this.activity != null) {
      this.isLoading = true;

      this.activiteService.update(this.activityId!, trueSendingActivity as any).subscribe({
        next: (createdStructure) => {
          localStorage.removeItem(this.activityDataKey);
          localStorage.removeItem(this.stepperStateKey);
          this.router.navigate([
            routeAlias.activities.home,
            routeAlias.activities.activity.home,
          ])
          this.isLoading = false
        },
        error: (error) => {
          console.error('Erreur lors de la création de la structure programmatique :', error);
          this.isLoading = false
        }
      });

    } else {
      console.warn('Le nom de la structure programmatique ne peut pas être vide.');
    }
  }
}